/**
 * Churn Deep Dive Tab
 * V6.15 V2: Detailed churn diagnostics with at-risk members table
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
} from '@mui/material';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import apiService from '../../../services/api';
import MemberLocation, { MemberLocationFields } from '../../common/MemberLocation';

interface ChurnData {
  kpis: {
    totalChurned: number;
    mrrLost: number;
    avgTenureDays: number;
    atRiskCount: number;
  };
  daily: { date: string; churned: number }[];
  byTier: { tier: string; displayName: string; churned: number; activeCount: number; churnRate: number }[];
  byTenure: { bucket: string; count: number }[];
  atRisk: ({ id: number; name: string; email: string; plan: string; status: string; expiresAt: string | null; lastActive: string } & MemberLocationFields)[];
}

const TARGET_BENCHMARKS: Record<string, number> = {
  standard: 5, premium: 3, legacy_creator: 2, vault_standard: 4, vault_premium: 3,
};

const KpiCard: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({ label, value, sub, color }) => (
  <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '24px', fontWeight: 800, color: color || '#111827', mt: 0.5 }}>
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#9CA3AF', mt: 0.25 }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

const formatDate = (d: string) => {
  const date = new Date(d + 'T00:00:00');
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

interface ChurnDeepDiveTabProps {
  timeRangeDays?: string;
}

const ChurnDeepDiveTab: React.FC<ChurnDeepDiveTabProps> = ({ timeRangeDays }) => {
  const [data, setData] = useState<ChurnData | null>(null);
  const [loading, setLoading] = useState(true);
  const days = timeRangeDays ?? '90';

  useEffect(() => {
    setLoading(true);
    const load = async () => {
      try {
        const response = await apiService.get(`/api/admin/subscriptions/intelligence/churn-deep-dive?days=${days}`);
        if (response.data) {
          setData(response.data as ChurnData);
        }
      } catch (e) {
        console.error('Failed to load churn deep dive:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [days]);

  if (loading) {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
          {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />)}
        </Box>
        <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', color: '#6B7280' }}>
          Unable to load churn data
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {/* KPI Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <KpiCard label="Total Churned" value={data.kpis.totalChurned.toLocaleString()} sub={`Last ${days} days`} color="#EF4444" />
        <KpiCard label="MRR Lost" value={`$${data.kpis.mrrLost.toFixed(2)}`} sub="From churned members" color="#EF4444" />
        <KpiCard label="Avg Tenure" value={`${data.kpis.avgTenureDays}d`} sub="Before cancellation" color="#6B7280" />
        <KpiCard label="At-Risk" value={data.kpis.atRiskCount.toLocaleString()} sub="Past due or expiring" color="#F59E0B" />
      </Box>

      {/* Churn Over Time */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 1.5 }}>
          Churn Over Time
        </Typography>
        <div style={{ width: '100%', height: 250, minHeight: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fontFamily: 'Montserrat' }} />
              <YAxis tick={{ fontSize: 10, fontFamily: 'Montserrat' }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontFamily: 'Montserrat', fontSize: 12, borderRadius: 8 }} labelFormatter={formatDate} />
              <defs>
                <linearGradient id="redGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="churned" stroke="#EF4444" fill="url(#redGrad)" strokeWidth={2} name="Churned" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {/* Churn by Tier */}
        <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 1.5 }}>
            Churn Rate by Tier
          </Typography>
          <div style={{ width: '100%', height: 220, minHeight: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.byTier} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis type="number" tick={{ fontSize: 10, fontFamily: 'Montserrat' }} tickFormatter={(v: number) => `${v}%`} />
                <YAxis type="category" dataKey="displayName" tick={{ fontSize: 10, fontFamily: 'Montserrat' }} width={100} />
                <Tooltip contentStyle={{ fontFamily: 'Montserrat', fontSize: 12, borderRadius: 8 }} formatter={(v: number) => [`${v}%`, 'Churn Rate']} />
                {data.byTier.map(t => {
                  const benchmark = TARGET_BENCHMARKS[t.tier] || 5;
                  return <ReferenceLine key={t.tier} x={benchmark} stroke="#D4A843" strokeDasharray="4 3" />;
                }).filter((_, i) => i === 0)}
                <Bar dataKey="churnRate" fill="#EF4444" radius={[0, 4, 4, 0]} name="Churn Rate" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Paper>

        {/* Churn by Tenure */}
        <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 1.5 }}>
            Churn by Account Tenure
          </Typography>
          <div style={{ width: '100%', height: 220, minHeight: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.byTenure.length > 0 ? data.byTenure : [
                { bucket: '0-30d', count: 0 },
                { bucket: '31-90d', count: 0 },
                { bucket: '91-180d', count: 0 },
                { bucket: '181-365d', count: 0 },
                { bucket: '365+d', count: 0 },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="bucket" tick={{ fontSize: 10, fontFamily: 'Montserrat' }} />
                <YAxis tick={{ fontSize: 10, fontFamily: 'Montserrat' }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontFamily: 'Montserrat', fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="count" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Churned" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Paper>
      </Box>

      {/* At-Risk Members Table */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 1.5 }}>
          At-Risk Members
        </Typography>
        {data.atRisk.length === 0 ? (
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#10B981', textAlign: 'center', py: 3 }}>
            No at-risk members detected — all subscriptions healthy
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Name', 'Email', 'Location', 'Plan', 'Status', 'Expires', 'Last Active'].map(h => (
                    <TableCell key={h} sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', borderBottom: '2px solid #E5E7EB' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.atRisk.map(user => (
                  <TableRow key={user.id} hover>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', fontWeight: 600 }}>{user.name}</TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#6B7280' }}>{user.email}</TableCell>
                    <TableCell><MemberLocation
                        location_country_code={user.location_country_code}
                        location_region={user.location_region}
                        location_city={user.location_city}
                        location_source={user.location_source}
                        location_confidence={user.location_confidence}
                        location_resolved_at={user.location_resolved_at}
                        compact
                      /></TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px' }}>
                      <Chip label={user.plan} size="small" sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 600, bgcolor: '#F3F4F6' }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        size="small"
                        sx={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: '10px',
                          fontWeight: 600,
                          bgcolor: user.status === 'past_due' ? '#FEF2F2' : '#FFF7ED',
                          color: user.status === 'past_due' ? '#EF4444' : '#F59E0B',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6B7280' }}>
                      {user.expiresAt ? new Date(user.expiresAt).toLocaleDateString() : '\u2014'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6B7280' }}>
                      {new Date(user.lastActive).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default ChurnDeepDiveTab;
